<template>
    <MContainer class="overflow-auto pt-2 w-full h-full" style="max-height: calc(100% - 1.25rem) !important; background: transparent">
        <MContainerRow v-if="showNoDataText">{{ $t("web-rg.noDataToDisplayClickImportcreateButtonToUploadItems") }}</MContainerRow>
        <MContainer class="grid w-full gap-3 grid-cols-[repeat(auto-fill,minmax(300px,1fr))]">
            <div v-for="(item, index) in model" :key="getItemKey(item, index)" :class="['input-item rounded-xl overflow-hidden h-[220px]', { 'doc-item': isDocumentItem(item) }]">
                <Image
                    :alt="item.title"
                    :src="getThumbnailSrc(item)"
                    :imageClass="getImageClass(item)"
                    class="block w-full h-full"
                    style="object-fit: cover"
                    :preview="!shouldShowHeifFallback(item, index)">
                    <template #previewicon>
                        <div
                            v-if="!item.progress && !shouldShowHeifFallback(item, index)"
                            class="w-full h-full flex justify-center items-center"
                            @click="openClicked($event, item, index)">
                            <Button
                                v-if="showOpen && !shouldShowHeifFallback(item, index)"
                                class="p-button-rounded p-button-outlined"
                                icon="pi pi-eye"
                                style="height: 35px !important; width: 35px !important"
                                @click="openClicked($event, item, index)" />
                            <Button
                                v-if="props.showShare"
                                class="p-button-rounded p-button-outlined p-button-raised ml-2"
                                icon="pi pi-share-alt"
                                style="height: 35px !important; width: 35px !important"
                                @click.stop="showDialog(item)" />
                            <a
                                href="#"
                                class="p-button p-button-rounded p-button-outlined ml-2 file-action-button"
                                @click.stop.prevent="downloadItemImage(item)"
                                :aria-label="`Download`">
                                <i class="pi pi-download"></i>
                            </a>
                            <a href="#" class="p-button p-button-rounded p-button-outlined ml-2 file-action-button" @click.stop.prevent="deleteItem(item)" :aria-label="`Delete`">
                                <i class="pi pi-trash"></i>
                            </a>
                        </div>
                    </template>

                    <template #image>
                        <!-- overlay text/progress -->
                        <div class="item-wrapper w-full pointer-events-none" style="z-index: 1000">
                            <div v-if="item.progress == null || item.progress === 0" class="flex justify-center">
                                <span :class="getSpanClass(item.filename)">
                                    {{ clampFilename(item.filename) }}
                                </span>
                            </div>
                            <div v-else>{{ $t("web-rg.uploading") }} ({{ item.progress.toFixed(1) }}%)</div>
                        </div>

                        <!-- image / skeleton -->
                        <div :class="getThumbnailWrapperClass(item)">
                            <div v-if="shouldShowHeifFallback(item, index)" class="heif-fallback">
                                <div class="heif-fallback-text">
                                    {{ t("shared.previewNotSupportedInBrowser") }}
                                </div>
                                <div v-if="!item.progress" class="heif-actions">
                                    <Button
                                        v-if="props.showShare"
                                        class="p-button-rounded p-button-outlined p-button-raised"
                                        icon="pi pi-share-alt"
                                        style="height: 35px !important; width: 35px !important"
                                        @click.stop="showDialog(item)" />
                                    <a
                                        href="#"
                                        class="p-button p-button-rounded p-button-outlined file-action-button"
                                        @click.stop.prevent="downloadItemImage(item)"
                                        :aria-label="`Download`">
                                        <i class="pi pi-download"></i>
                                    </a>
                                    <a
                                        href="#"
                                        class="p-button p-button-rounded p-button-outlined file-action-button"
                                        @click.stop.prevent="deleteItem(item)"
                                        :aria-label="`Delete`">
                                        <i class="pi pi-trash"></i>
                                    </a>
                                </div>
                            </div>
                            <div
                                v-else-if="!item.thumbnailImageSrc && !isZipItem(item)"
                                class="w-full h-full rounded-lg animate-pulse bg-gradient-to-r from-gray-800 via-gray-700 to-gray-800" />
                            <img
                                v-else
                                :alt="item.title"
                                :src="getThumbnailSrc(item)"
                                :class="[getThumbnailClass(item), getImageClass(item)]"
                                draggable="false"
                                @error="onThumbnailError(item, index)" />
                        </div>
                    </template>
                </Image>
            </div>
        </MContainer>
    </MContainer>
</template>
<script lang="ts" setup>
import Image from "primevue/image";
import { nextTick, ref, computed, onMounted } from "vue";
import { MContainer, MContainerRow, ImageFileModel, isMeshFileFormat, replaceSuffix, useClientInfo } from "@xlabmedical/medic-web-framework";
import RgPdfPreviewDialog from "@/components/RgPdfPreviewDialog.vue";
import RgTextPreviewDialog from "@/components/RgTextPreviewDialog.vue";
import { ProjectFileType } from "@xlabmedical/medic-api";
import { useDialog } from "primevue/usedialog";
import { useI18n } from "vue-i18n";
import { UAParser } from "ua-parser-js";

const { t } = useI18n();

const dialog = useDialog();

const dialogVisible = ref(false);
const imageModel = ref();
const props = withDefaults(
    defineProps<{
        model: ImageFileModel[];
        showOpen: boolean;
        showShare: boolean;
        emitOpen?: boolean;
        patientId?: string;
    }>(),
    {
        showOpen: true,
        showShare: false,
        emitOpen: false,
        patientId: undefined,
    },
);

const browser = ref<Record<string, string>>({});
const { collectClientSignals } = useClientInfo(() => UAParser());
const previewFailures = ref<Record<string, boolean>>({});
const isSafariBrowser = computed(() => (browser.value?.browserName ?? "") === "Safari");

onMounted(() => {
    browser.value = collectClientSignals();
});

const emits = defineEmits<{
    (e: "sendMediaMessage", model: ImageFileModel, message: string): void;
    (e: "open", model: ImageFileModel): void;
    (e: "delete", model: ImageFileModel): void;
}>();

const showNoDataText = computed(() => {
    return props.model.length === 0;
});

const itemStyle = (filename: string) => {
    return filename.endsWith(".pdf") ? "" : "width: 32% !important";
};

function getSpanClass(filename: string) {
    const classSpan = ["ml-2 font-bold text-2xl"];

    return classSpan;
}

const getItemKey = (item: ImageFileModel, index: number) => {
    return item.filename || item.title || `item-${index}`;
};

const isHeifItem = (item: ImageFileModel) => {
    const name = item.filename?.toLowerCase() || "";
    return name.endsWith(".heif") || name.endsWith(".heic");
};

const onThumbnailError = (item: ImageFileModel, index: number) => {
    if (!isHeifItem(item) || isSafariBrowser.value) return;
    const key = getItemKey(item, index);
    previewFailures.value = { ...previewFailures.value, [key]: true };
};

const shouldShowHeifFallback = (item: ImageFileModel, index: number) => {
    if (isSafariBrowser.value || !isHeifItem(item)) return false;
    const key = getItemKey(item, index);
    return Boolean(previewFailures.value[key]);
};

function sendMediaMessage(model: ImageFileModel, message: string) {
    emits("sendMediaMessage", model, message);
}

function deleteItem(model: ImageFileModel) {
    emits("delete", model);
}

function showDialog(item: ImageFileModel) {
    dialogVisible.value = false;
    imageModel.value = item;

    //hack for stl to show img in dialog
    if (isMeshFileFormat(item.filename)) {
        item.itemImageSrc = replaceSuffix(item.itemImageSrc, ".png");
    }
    nextTick(() => {
        dialogVisible.value = true;
    });
}

async function openClicked(e: MouseEvent, item: ImageFileModel, index?: number) {
    if (index !== undefined && shouldShowHeifFallback(item, index)) {
        return;
    }
    if (item.filename.endsWith(".pdf")) {
        e.stopPropagation();

        await onOpenPDFClick(item);
    } else if (item.filename.endsWith(".txt")) {
        e.stopPropagation();

        await onOpenTXTClick(item);
    } else if (props.emitOpen) {
        e.stopPropagation();
        emits("open", item);
    }
}

function clampFilename(filename: string) {
    if (filename.length > 35) {
        return `${filename.substring(0, 35)}...`;
    } else {
        return filename;
    }
}

type HeadersMap = Record<string, string>;
type ItemImageSrc = string | { url: string; headers?: HeadersMap };

// --- helpers ---
const filenameFromDisposition = (disposition?: string, fallback = "image.png") => {
    if (!disposition) return fallback;
    const m = /filename\*?=(?:UTF-8''|")?([^";]+)"?/i.exec(disposition);
    try {
        return m ? decodeURIComponent(m[1]) : fallback;
    } catch {
        return fallback;
    }
};

const triggerDownload = (blob: Blob, filename: string) => {
    const href = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = href;
    a.download = filename || "image.png";
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(href), 0);
};

const downloadFromItemSrc = async (src: ItemImageSrc, fallbackName = "image.png") => {
    const { url, headers } = typeof src === "string" ? { url: src, headers: undefined } : src;

    const res = await fetch(url, {
        headers,
        mode: "cors",
        // if your auth is cookie-based, also add:
        // credentials: "include",
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);

    const blob = await res.blob();
    const name = filenameFromDisposition(res.headers.get("content-disposition") || undefined, fallbackName) || fallbackName;

    triggerDownload(blob, name);
};

const downloadItemImage = async (item: ImageFileModel) => {
    const filename = item?.type === ProjectFileType.Image ? item?.filename : item?.filename?.replace(/\.png$/i, ".stl");

    await downloadFromItemSrc(item.itemImageSrc as ItemImageSrc, filename);
};

function isDocumentItem(item: ImageFileModel) {
    const name = item.filename?.toLowerCase() || "";
    return (
        name.endsWith(".pdf") ||
        name.endsWith(".txt") ||
        name.endsWith(".xml") ||
        name.endsWith(".constructioninfo") ||
        name.endsWith(".docx") ||
        name.endsWith(".xlsx") ||
        name.endsWith(".zip") ||
        item.type === ProjectFileType.LiabilityForm
    );
}

function isZipItem(item: ImageFileModel) {
    const name = item.filename?.toLowerCase() || "";
    return name.endsWith(".zip");
}

function getImageClass(item: ImageFileModel) {
    return isDocumentItem(item) ? "p-img-document" : "p-img-image";
}

function getThumbnailSrc(item: ImageFileModel) {
    if (isZipItem(item)) {
        return new URL("/img/zip_file_icon.png", import.meta.url).href;
    }

    const name = item.filename?.toLowerCase() || "";
    if (name.endsWith(".txt")) {
        return new URL("/img/txt_file_icon.png", import.meta.url).href;
    }
    if (name.endsWith(".xml") || name.endsWith(".constructioninfo")) {
        return new URL("/img/xml_file_icon.png", import.meta.url).href;
    }

    return item.thumbnailImageSrc;
}

function getThumbnailClass(item: ImageFileModel) {
    const base = "rounded-lg";
    return isDocumentItem(item) ? `${base} block w-full h-full` : `${base} w-full h-full object-cover`;
}

function getThumbnailWrapperClass(item: ImageFileModel) {
    return isDocumentItem(item) ? "w-full h-full doc-thumb-wrap" : "w-full h-full";
}

const fetchBlobFromItemSrc = async (src: ItemImageSrc) => {
    const { url, headers } = typeof src === "string" ? { url: src, headers: undefined } : src;

    const res = await fetch(url, { headers, mode: "cors" });
    if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
    }

    return res.blob();
};

const onOpenPDFClick = async (itemModel: ImageFileModel) => {
    let objectUrl: string | undefined;
    try {
        const blob = await fetchBlobFromItemSrc(itemModel.itemImageSrc as ItemImageSrc);
        objectUrl = URL.createObjectURL(blob);
        const isShortScreen = typeof window !== "undefined" && window.innerHeight < 1200;
        const dialogHeight = isShortScreen ? "85vh" : "90vh";
        const viewerHeight = isShortScreen ? "60vh" : "72vh";
        dialog.open(RgPdfPreviewDialog, {
            props: {
                closable: true,
                modal: true,
                header: t("weblink.pdfPreview"),
                style: { width: "110vh", height: dialogHeight },
            },
            data: {
                src: objectUrl,
                height: viewerHeight,
            },
            onClose: () => {
                if (objectUrl) {
                    URL.revokeObjectURL(objectUrl);
                    objectUrl = undefined;
                }
            },
        });
    } catch (err) {
        if (objectUrl) {
            URL.revokeObjectURL(objectUrl);
        }
        console.error("Error while opening PDF preview", err);
    }
};

const onOpenTXTClick = async (itemModel: ImageFileModel) => {
    try {
        const blob = await fetchBlobFromItemSrc(itemModel.itemImageSrc as ItemImageSrc);
        const text = await blob.text();

        dialog.open(RgTextPreviewDialog, {
            props: {
                closable: true,
                modal: true,
                header: itemModel.title || itemModel.filename || t("web-rg.textPreview"),
                style: {
                    width: "110vh",
                },
            },
            data: {
                text,
                height: typeof window !== "undefined" && window.innerHeight < 1200 ? "60vh" : "72vh",
            },
        });
    } catch (err) {
        console.error("Error while opening text preview", err);
    }
};
</script>
<style lang="scss">
:deep(.p-image img) {
    border-radius: 0.75rem;
    object-fit: cover;
    width: 100%;
    height: 100%;
    background: var(--webcad-dropdown-bg-color) !important;
    min-height: 17rem !important;
}

.p-img-document {
    object-fit: contain;
}

.p-image-preview-container {
    max-height: 17rem;
    height: 100%;
}

.p-image {
    height: 100%;
}

.center {
    display: block;
    margin-left: auto;
    margin-right: auto;
    width: 50%;
}

.input-item {
    position: relative;
    max-height: 17rem;
    border: 1px solid rgba(255, 255, 255, 0.08);
}

.input-item.doc-item {
    background: var(--webcad-dropdown-bg-color) !important;
}

.input-item .item-wrapper {
    position: absolute;
    top: 3%;
    left: 1%;
    display: inline-block;
}

.doc-thumb-wrap {
    background: var(--webcad-dropdown-bg-color) !important;
    border-radius: 0.75rem;
}

.heif-fallback {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 0 1rem;
    gap: 0.75rem;
    color: var(--webcad-text-color, #ffffff);
    font-weight: 600;
}

.heif-actions {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.2s ease;
}

.input-item:hover .heif-actions {
    opacity: 1;
    pointer-events: auto;
}

.flex.justify-center.pt-4 {
    padding-top: 0rem !important;
    padding-bottom: 1.5rem;
}

.file-action-button {
    width: 35px;
    height: 35px;
    padding: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
}
</style>
